import React, {useState, useEffect} from 'react';
import {Button, Form, Input, Select, Modal, AutoComplete, List, Row, Col, Table} from 'antd';
import {
    CommandDto,
    RequestControllerApi,
    RequestDto, SystemDto
} from '../../../api';
import {getPathvariables} from "../../../utils/Utils";
import {DeleteOutlined, FormOutlined} from "@ant-design/icons";
import {useKeycloakHeader} from "../../../config/Config";
import {useSystemContext} from "../../../utils/hook/SystemProvider";

const layout = {
    labelCol: {span: 8},
    wrapperCol: {span: 16},
};

const tailLayout = {
    wrapperCol: {offset: 8, span: 16},
};

interface VariablesMap {
    id: number,
    name: string,
    value: string
}

export interface ResponseVariable {
    key: string,
    value: string
}

const RequestCommandModal = ({onCommandCardData}) => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [requests, setRequests] = useState<RequestDto[]>([]);
    const [selectedRequest, setSelectedRequest] = useState<RequestDto>();
    const [searchValue, setSearchValue] = useState('');
    const [selectedSystem, setSelectedSystem] = useState<SystemDto | undefined>();
    const [pathVariables, setPathVariables] = useState<VariablesMap[]>([]);
    const [responseVariables, setResponseVariables] = useState<{ [key: string]: string; }>({});
    const [inputPathVariables, setInputPathVariables] = useState({});
    const [newKey, setNewKey] = useState("");
    const [newValue, setNewValue] = useState("");

    const headers = useKeycloakHeader();
    const requestControllerApi = new RequestControllerApi();
    const { systems, triggerRefresh } = useSystemContext();
    const {Option} = Select;




    const columns = [
        {
            title: 'Variable',
            dataIndex: 'key',
            key: 'key',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
        },
        {
            title: 'Delete',
            key: 'delete',
            render: (text, record) => (
                <Button type="primary" danger onClick={() => removeVariable(record.key)}><DeleteOutlined/></Button>
            ),
        },
    ];

    const handleSubmitResponseVariable = () => {
        if (newKey && newValue) {
            addNewResponseVariable({ key: newKey, value: newValue });
            setNewKey("");
            setNewValue("");
        }
    };

    const addNewResponseVariable = (variable: ResponseVariable) => {
        setResponseVariables({
            ...responseVariables,
            [variable.key]: variable.value,
        });
    }

    const removeVariable = (key: string) => {
        // Erstelle eine Kopie des aktuellen Zustands
        const updatedVariables = { ...responseVariables };

        // Überprüfe, ob der Key im State existiert
        if (updatedVariables.hasOwnProperty(key)) {
            // Key aus der Kopie entfernen
            delete updatedVariables[key];

            // Den State mit der aktualisierten Kopie aktualisieren
            setResponseVariables(updatedVariables);
        }
    };


    useEffect(() => {
        if (isModalVisible) {
            fetchRequests();
        }
    }, [isModalVisible, selectedSystem]);

    const fetchRequests = async () => {
        if (!selectedSystem || !selectedSystem.name) {
            return;
        }

        requestControllerApi
            .getAllRequestsBySystem(selectedSystem.name, headers)
            .then((axiosResponse) => {
                const axiosData: Array<RequestDto> = axiosResponse.data;
                if (axiosResponse.status === 200) {
                    setRequests(axiosData);
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const onFinish = (values) => {
        const pathVariablesKeyValues = getKeyValuesPairs(pathVariables, inputPathVariables);


        const command: CommandDto = {
            requestId: selectedRequest?.id,
            requestUrl: values.requestUrl,
            pathVariables: pathVariablesKeyValues,
            responseVariables: responseVariables,
            requestJson: values.requestJson,
            requestName: selectedRequest?.name,
            commandType: 'REQUEST',
        };

        onCommandCardData(command);
        setIsModalVisible(false);
        form.resetFields();
    };

    const onReset = () => {
        form.resetFields();
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSearch = (value) => {
        setSearchValue(value);
    };

    const onSelect = (value, option) => {
        const selected = requests.find((p) => p.name === value);
        setSelectedRequest(selected);
        form.setFieldValue("requestUrl", selected?.baseUrl);
        const pathVariablesMap = getPathvariables(selected?.baseUrl)
            .map((p, index) => ({"id": index, "name": p} as VariablesMap));

        setPathVariables(pathVariablesMap);
    };

    const handleSystemChange = (value) => {
        setSelectedSystem(value);
    };

    const options = requests
        .filter((request) =>
            request.name ? request.name.toLowerCase().includes(searchValue.toLowerCase()) : null
        )
        .map((request) => ({
            value: request.name,
            label: request.name,
        }));

    const handleInputChangePathVariables = (event, id) => {
        const {value} = event.target;
        setInputPathVariables({...inputPathVariables, [id]: value});
    };

    const getKeyValuesPairs = (variables, inputs) => {
        const keyValuePaare = {};
        for (const element of variables) {
            const inputValue = inputs[element.id];
            if (inputValue) {
                keyValuePaare[element.name] = inputValue;
            }
        }
        return keyValuePaare;
    };

    return (
        <div>
            <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'stretch'}}>
                <Button disabled={true} type="primary" onClick={showModal} style={{width: '100%', height: '100%'}}>
                    <FormOutlined/> API
                </Button>
            </div>
            <Modal title="Add Request" open={isModalVisible} onCancel={handleCancel} footer={null}>
                <Form {...layout} form={form} name="control-hooks" onFinish={onFinish} style={{maxWidth: 600}}>
                    <Form.Item name="system" label="System">
                        <Select onChange={handleSystemChange}>
                            {systems.map((option) => (
                                <Option key={option.id} value={option.id}>
                                    {option.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="requestId" label="Request">
                        <AutoComplete
                            options={options}
                            onSelect={onSelect}
                            onSearch={handleSearch}
                            placeholder="Search for a Request"
                        />
                    </Form.Item>
                    <Form.Item name="requestUrl" label="Request URL">
                        <Input/>
                    </Form.Item>
                    <Form.Item name="requestJson" label="Request JSON">
                        <Input.TextArea rows={4}/>
                    </Form.Item>
                    <hr style={{ border: '1px solid #ccc', margin: '16px 0' }} />
                    <div style={{ textAlign: 'center' }}>
                        <h2>Path-Variables</h2>
                    </div>
                    <List
                        itemLayout="horizontal"
                        dataSource={pathVariables}
                        renderItem={(element) => (
                            <List.Item>
                                <List.Item.Meta
                                    description={
                                        <div>
                                            <Row gutter={16}>
                                                <Col span={5} style={{textAlign: 'right'}}>
                                                    <span>{element.name}</span>
                                                </Col>
                                                <Col span={19}>
                                                    <Input
                                                        placeholder="Value"
                                                        onChange={(event) => handleInputChangePathVariables(event, element.id)}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                    <hr style={{ border: '1px solid #ccc', margin: '16px 0' }} />
                    <div style={{ textAlign: 'center' }}>
                        <h2>Response-Variables</h2>
                    </div>
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Table columns={columns} dataSource={Object.entries(responseVariables)
                                .map(([key, value]) => ({ key, value }))} pagination={false} />
                        </Col>
                        <Col span={12}>
                            <Input
                                placeholder="Response-Key"
                                value={newKey}
                                onChange={(e) => setNewKey(e.target.value)}
                            />
                        </Col>
                        <Col span={12}>
                            <Input
                                placeholder="Save Variable"
                                value={newValue}
                                onChange={(e) => setNewValue(e.target.value)}
                            />
                        </Col>
                        <Col span={24} style={{ textAlign: 'center' }}>
                            <Button onClick={handleSubmitResponseVariable}>Add</Button>
                        </Col>
                    </Row>
                    <hr style={{ border: '1px solid #ccc', margin: '16px 0' }} />
                    <Form.Item {...tailLayout}>
                        <Button type="primary" htmlType="submit">
                            Add
                        </Button>
                        <Button htmlType="button" onClick={onReset}>
                            Reset
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default RequestCommandModal;